/**
 * OilServicePanel - Oil Service Specification Confirmation Workflow
 * 
 * Displays:
 * 1. Vehicle Identification (from job context)
 * 2. Oil Service Requirements
 * 3. Advisor Job Guide
 * 4. Oil Service Specification Check (confirmation checklist)
 */

import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { 
  AlertTriangle, 
  CheckCircle2, 
  Car, 
  Droplets, 
  BookOpen, 
  ClipboardCheck,
  ExternalLink,
  Info
} from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const OilServicePanel = ({ 
  jobGroup, 
  yestimateId, 
  vehicleInfo, 
  onConfirmationComplete 
}) => {
  const [mapping, setMapping] = useState(null);
  const [confirmation, setConfirmation] = useState({
    oil_spec_confirmed: false,
    oil_capacity_confirmed: false,
    oil_filter_included: false,
    service_reset_required: false,
    confirmed_spec: '',
    confirmed_capacity: '',
    advisor_notes: ''
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  
  const serviceItemId = jobGroup?.interval_item_id || 'oil_filter_service';
  
  // Fetch mapping and existing confirmation
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Get service mapping with vehicle context
        const mappingRes = await axios.get(`${API}/api/service-mappings/${serviceItemId}`, {
          params: {
            vehicle_make: vehicleInfo?.make,
            vehicle_model: vehicleInfo?.model,
            fuel_type: vehicleInfo?.fuel_type,
            year: vehicleInfo?.year
          }
        });
        setMapping(mappingRes.data);
        
        // Get existing confirmation if any
        const confirmRes = await axios.get(
          `${API}/api/oil-spec-confirmation/${yestimateId}/${serviceItemId}`
        );
        if (confirmRes.data) {
          setConfirmation(prev => ({
            ...prev,
            ...confirmRes.data
          }));
        }
      } catch (err) {
        console.error('Failed to fetch oil service data:', err);
      }
      setIsLoading(false);
    };
    
    if (yestimateId && serviceItemId) {
      fetchData();
    }
  }, [yestimateId, serviceItemId, vehicleInfo]);
  
  // Save confirmation
  const saveConfirmation = async () => {
    setIsSaving(true);
    try {
      const res = await axios.post(`${API}/api/oil-spec-confirmation`, {
        job_id: jobGroup?.id,
        yestimate_id: yestimateId,
        service_item_id: serviceItemId,
        ...confirmation,
        confirmed_at: new Date().toISOString()
      });
      
      if (res.data.is_confirmed && onConfirmationComplete) {
        onConfirmationComplete(res.data);
      }
    } catch (err) {
      console.error('Failed to save confirmation:', err);
    }
    setIsSaving(false);
  };
  
  const isAllConfirmed = confirmation.oil_spec_confirmed && 
    confirmation.oil_capacity_confirmed && 
    confirmation.oil_filter_included && 
    confirmation.service_reset_required;
  
  if (isLoading) {
    return (
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="py-4">
          <div className="animate-pulse flex items-center gap-2">
            <Droplets className="w-5 h-5 text-blue-500" />
            <span className="text-blue-700">Loading oil service specifications...</span>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* 1. Vehicle Identification */}
      <Card className="border-slate-200">
        <CardHeader className="py-3 bg-slate-50">
          <CardTitle className="text-sm flex items-center gap-2">
            <Car className="w-4 h-4 text-slate-600" />
            Vehicle Identification
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <span className="text-slate-500">Make:</span>
              <p className="font-medium">{vehicleInfo?.make || 'N/A'}</p>
            </div>
            <div>
              <span className="text-slate-500">Model:</span>
              <p className="font-medium">{vehicleInfo?.model || 'N/A'}</p>
            </div>
            <div>
              <span className="text-slate-500">Year:</span>
              <p className="font-medium">{vehicleInfo?.year || 'N/A'}</p>
            </div>
            <div>
              <span className="text-slate-500">Fuel:</span>
              <p className="font-medium">{vehicleInfo?.fuel_type || 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* 2. Oil Service Requirements */}
      <Card className="border-blue-200 bg-blue-50/30">
        <CardHeader className="py-3 bg-blue-100/50">
          <CardTitle className="text-sm flex items-center gap-2">
            <Droplets className="w-4 h-4 text-blue-600" />
            Oil Service Requirements
          </CardTitle>
        </CardHeader>
        <CardContent className="py-3 space-y-3">
          <div className="grid gap-3">
            <div className="flex justify-between items-center p-2 bg-white rounded border">
              <span className="text-sm font-medium">Manufacturer oil approval required:</span>
              <span className="text-sm">
                {mapping?.oil_spec_required || (
                  <span className="text-amber-600 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Confirm via HaynesPro / Autodata
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded border">
              <span className="text-sm font-medium">Typical oil grade:</span>
              <span className="text-sm">
                {mapping?.oil_grade || (
                  <span className="text-amber-600 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Confirm via HaynesPro / Autodata
                  </span>
                )}
              </span>
            </div>
            <div className="flex justify-between items-center p-2 bg-white rounded border">
              <span className="text-sm font-medium">Oil capacity:</span>
              <span className="text-sm">
                {mapping?.oil_capacity_litres ? `${mapping.oil_capacity_litres}L` : (
                  <span className="text-amber-600 flex items-center gap-1">
                    <Info className="w-3 h-3" />
                    Confirm via HaynesPro / Autodata
                  </span>
                )}
              </span>
            </div>
          </div>
          
          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" className="text-xs" asChild>
              <a href="https://www.haynespro.com" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3 h-3 mr-1" />
                HaynesPro
              </a>
            </Button>
            <Button variant="outline" size="sm" className="text-xs" asChild>
              <a href="https://www.autodata-group.com" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-3 h-3 mr-1" />
                Autodata
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>
      
      {/* 3. Advisor Job Guide */}
      {mapping?.job_guide && mapping.job_guide.length > 0 && (
        <Card className="border-emerald-200 bg-emerald-50/30">
          <CardHeader className="py-3 bg-emerald-100/50">
            <CardTitle className="text-sm flex items-center gap-2">
              <BookOpen className="w-4 h-4 text-emerald-600" />
              Advisor Job Guide
            </CardTitle>
          </CardHeader>
          <CardContent className="py-3">
            <ul className="space-y-2">
              {mapping.job_guide.sort((a, b) => a.order - b.order).map((guide, idx) => (
                <li 
                  key={idx}
                  className={`flex items-start gap-2 text-sm ${
                    guide.is_warning ? 'text-amber-700 bg-amber-50 p-2 rounded border border-amber-200' : ''
                  }`}
                >
                  {guide.is_warning ? (
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                  )}
                  <span>{guide.text}</span>
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
      
      {/* 4. Oil Service Specification Check */}
      {mapping?.requires_spec_confirmation && (
        <Card className={`border-2 ${isAllConfirmed ? 'border-green-400 bg-green-50/50' : 'border-amber-400 bg-amber-50/50'}`}>
          <CardHeader className={`py-3 ${isAllConfirmed ? 'bg-green-100/50' : 'bg-amber-100/50'}`}>
            <CardTitle className="text-sm flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ClipboardCheck className={`w-4 h-4 ${isAllConfirmed ? 'text-green-600' : 'text-amber-600'}`} />
                Oil Service Specification Check
              </div>
              {isAllConfirmed && (
                <Badge className="bg-green-600 text-white">
                  <CheckCircle2 className="w-3 h-3 mr-1" />
                  Confirmed
                </Badge>
              )}
            </CardTitle>
            {!isAllConfirmed && (
              <p className="text-xs text-amber-700 mt-1">
                Before finalising the estimate, confirm the following specifications.
              </p>
            )}
          </CardHeader>
          <CardContent className="py-4 space-y-4">
            {/* Checklist */}
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <Checkbox
                  id="oil_spec_confirmed"
                  checked={confirmation.oil_spec_confirmed}
                  onCheckedChange={(checked) => setConfirmation({
                    ...confirmation,
                    oil_spec_confirmed: checked
                  })}
                />
                <Label htmlFor="oil_spec_confirmed" className="text-sm cursor-pointer">
                  Manufacturer oil specification confirmed
                </Label>
              </div>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="oil_capacity_confirmed"
                  checked={confirmation.oil_capacity_confirmed}
                  onCheckedChange={(checked) => setConfirmation({
                    ...confirmation,
                    oil_capacity_confirmed: checked
                  })}
                />
                <Label htmlFor="oil_capacity_confirmed" className="text-sm cursor-pointer">
                  Oil capacity confirmed
                </Label>
              </div>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="oil_filter_included"
                  checked={confirmation.oil_filter_included}
                  onCheckedChange={(checked) => setConfirmation({
                    ...confirmation,
                    oil_filter_included: checked
                  })}
                />
                <Label htmlFor="oil_filter_included" className="text-sm cursor-pointer">
                  Oil filter replacement included
                </Label>
              </div>
              
              <div className="flex items-center gap-3">
                <Checkbox
                  id="service_reset_required"
                  checked={confirmation.service_reset_required}
                  onCheckedChange={(checked) => setConfirmation({
                    ...confirmation,
                    service_reset_required: checked
                  })}
                />
                <Label htmlFor="service_reset_required" className="text-sm cursor-pointer">
                  Service indicator reset required
                </Label>
              </div>
            </div>
            
            {/* Optional: Confirmed values */}
            <div className="grid grid-cols-2 gap-3 pt-2 border-t">
              <div>
                <Label className="text-xs text-slate-500">Confirmed Spec (optional)</Label>
                <Input
                  placeholder="e.g., VW 504 00"
                  value={confirmation.confirmed_spec}
                  onChange={(e) => setConfirmation({
                    ...confirmation,
                    confirmed_spec: e.target.value
                  })}
                  className="text-sm"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-500">Confirmed Capacity (optional)</Label>
                <Input
                  placeholder="e.g., 5.2L"
                  value={confirmation.confirmed_capacity}
                  onChange={(e) => setConfirmation({
                    ...confirmation,
                    confirmed_capacity: e.target.value
                  })}
                  className="text-sm"
                />
              </div>
            </div>
            
            {/* Advisor notes */}
            <div>
              <Label className="text-xs text-slate-500">Advisor Notes (optional)</Label>
              <Textarea
                placeholder="Any additional notes..."
                value={confirmation.advisor_notes}
                onChange={(e) => setConfirmation({
                  ...confirmation,
                  advisor_notes: e.target.value
                })}
                className="text-sm h-16"
              />
            </div>
            
            {/* Reference and Save */}
            <div className="flex items-center justify-between pt-2">
              <span className="text-xs text-slate-500">
                Reference: HaynesPro / Autodata
              </span>
              <Button 
                onClick={saveConfirmation}
                disabled={isSaving}
                className={isAllConfirmed ? 'bg-green-600 hover:bg-green-700' : ''}
              >
                {isSaving ? 'Saving...' : isAllConfirmed ? '✓ Save Confirmation' : 'Save Progress'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Confirmation Complete Message */}
      {isAllConfirmed && (
        <div className="flex items-center gap-2 p-3 bg-green-100 border border-green-300 rounded-lg">
          <CheckCircle2 className="w-5 h-5 text-green-600" />
          <span className="text-green-800 font-medium">
            ✔ Oil service specifications confirmed
          </span>
        </div>
      )}
    </div>
  );
};

export default OilServicePanel;
